import { useCallback, useEffect, useRef, useState } from 'react';

export interface AsyncResource<T> {
  data: T | null;
  isLoading: boolean;
  error: string | null;
  /** Recharge la ressource ; à appeler après toute mutation. */
  reload: () => Promise<void>;
}

/**
 * Chargement d'une ressource distante avec état de chargement, erreur et rechargement.
 *
 * Chaque onglet de l'ancien back-office réimplémentait ce trio (`isLoading` / `error` /
 * `setXxx`) à la main, souvent sans garde contre les réponses obsolètes. Le compteur de
 * requête ci-dessous ignore toute réponse arrivée après un rechargement plus récent.
 *
 * @param fetcher requête à exécuter ; doit être stable (useCallback) sous peine de boucle.
 * @param enabled passe à false tant que les paramètres requis ne sont pas connus.
 */
export const useAsyncResource = <T,>(
  fetcher: () => Promise<T>,
  enabled: boolean = true
): AsyncResource<T> => {
  const [data, setData] = useState<T | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(enabled);
  const [error, setError] = useState<string | null>(null);

  const requestIdRef = useRef(0);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const load = useCallback(async () => {
    if (!enabled) {
      setIsLoading(false);
      return;
    }

    const requestId = ++requestIdRef.current;
    setIsLoading(true);
    setError(null);

    try {
      const result = await fetcher();
      if (isMountedRef.current && requestId === requestIdRef.current) {
        setData(result);
      }
    } catch (err) {
      if (isMountedRef.current && requestId === requestIdRef.current) {
        console.error('Chargement impossible', err);
        setError('Impossible de charger ces données');
      }
    } finally {
      if (isMountedRef.current && requestId === requestIdRef.current) {
        setIsLoading(false);
      }
    }
  }, [fetcher, enabled]);

  useEffect(() => {
    void load();
  }, [load]);

  return { data, isLoading, error, reload: load };
};
